import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { STONE_TILE, type StoneTextures } from './door-textures.js';

/**
 * The door's masonry as real geometry: every brick is its own bevelled block, standing out from a recessed mortar bed by its own
 * amount, a little off true, with a stone-coloured tint and its own window onto the stone texture. Some have a corner broken off.
 * Bricks are drawn as a handful of instanced meshes, so a whole slab is a few draw calls however many there are.
 */

/** Row height including the mortar joint, the joint's width, and how deep each block is (most of it buried in the mortar). */
const ROW = .5, GAP = .075, DEPTH = .3;

export interface BrickOptions {
  /** +1 builds outward to the right of the seam, -1 to the left. */
  side: 1 | -1;
  width: number;
  height: number;
  /** Where the highest bricks' faces sit; the rest stand back from it by up to `relief`. */
  front: number;
  relief: number;
  seed: number;
  stone: StoneTextures;
  /** True where a brick box (x0, y0, x1, y1) would foul something cut into the slab, such as a socket. */
  keepOut: (x0: number, y0: number, x1: number, y1: number) => boolean;
  own: <T extends { dispose(): void }>(resource: T) => T;
}

function seeded(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A unit block with the corner at (sx, sy) broken off along a flat plane. */
function chipped(base: THREE.BufferGeometry, sx: number, sy: number): THREE.BufferGeometry {
  const geometry = base.clone(), position = geometry.getAttribute('position'), normal = geometry.getAttribute('normal');
  const facet = new THREE.Vector3(sx * .7, sy * .7, .25).normalize();
  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i) * sx, y = position.getY(i) * sy, over = x + y - .74;
    if (over <= 0) continue;
    position.setXY(i, (x - over / 2) * sx, (y - over / 2) * sy); normal.setXYZ(i, facet.x, facet.y, facet.z);
  }
  return geometry;
}

/** One slab's worth of bricks, in the slab's own space: the seam at x = 0, the face toward +z. */
export function buildBricks(options: BrickOptions): THREE.Group {
  const { side, width, height, front, relief, stone, keepOut, own } = options, random = seeded(options.seed);
  const between = (a: number, b: number) => a + random() * (b - a);

  // The stone is drawn once; every brick looks into its own window of it. Bricks are tinted per brick, and the mapping is per instance.
  stone.map.repeat.set(1, 1); stone.normalMap.repeat.set(1, 1);
  const material = own(new THREE.MeshStandardMaterial({ map: stone.map, normalMap: stone.normalMap, normalScale: new THREE.Vector2(1.5, 1.5), roughness: .88 }));
  material.customProgramCacheKey = () => 'door-brick';
  material.onBeforeCompile = shader => {
    shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nattribute vec4 brickUv;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\nvMapUv = uv * brickUv.zw + brickUv.xy;\nvNormalMapUv = vMapUv;');
  };

  // Courses of blocks working out from the seam: random widths, with joints kept clear of the course below.
  interface Brick { x0: number; x1: number; y: number }
  const bricks: Brick[] = [], rows = Math.ceil(height / ROW);
  let below: number[] = [];
  for (let row = 0; row < rows; row++) {
    let edges: number[] = [];
    for (let attempt = 0; attempt < 30; attempt++) {
      edges = [0]; let at = between(.35, 1.1) * (row % 2 ? .7 : 1) + (attempt > 20 ? between(0, .3) : 0);
      for (; at < width; at += between(.8, 1.5)) edges.push(at);
      edges.push(width);
      if (edges.slice(1, -1).every(e => below.every(b => Math.abs(e - b) > .22))) break;
    }
    below = edges;
    const y = -height / 2 + ROW * (row + .5);
    for (let i = 0; i + 1 < edges.length; i++) bricks.push({ x0: edges[i], x1: edges[i + 1], y });
  }

  // Each brick goes into the plain mesh or one of four with a different corner broken off.
  const base = own(new RoundedBoxGeometry(1, 1, 1, 2, .09));
  const variants = [base, ...[[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([sx, sy]) => own(chipped(base, sx, sy)))];
  const placed: THREE.Matrix4[][] = variants.map(() => []);
  const tints: THREE.Color[][] = variants.map(() => []), windows: number[][] = variants.map(() => []);
  const dummy = new THREE.Object3D();
  for (const brick of bricks) {
    const [lo, hi] = [side * brick.x0, side * brick.x1].sort((a, b) => a - b);
    if (keepOut(lo, brick.y - ROW / 2, hi, brick.y + ROW / 2)) continue;
    const w = brick.x1 - brick.x0 - GAP, h = ROW - GAP, variant = random() < .14 ? 1 + Math.floor(random() * 4) : 0;
    // How proud it stands is mostly near the top of the range, with the odd worn-back block.
    const face = front - relief * random() ** 2.2;
    dummy.position.set(side * (brick.x0 + brick.x1) / 2, brick.y + between(-.012, .012), face - DEPTH / 2);
    dummy.rotation.set(between(-.03, .03), between(-.03, .03), between(-.016, .016)); dummy.scale.set(w, h, DEPTH); dummy.updateMatrix();
    placed[variant].push(dummy.matrix.clone());
    tints[variant].push(new THREE.Color().setHSL(between(.07, .12), between(.05, .17), between(.34, .62)));
    windows[variant].push(random(), random(), w / STONE_TILE, h / STONE_TILE);
  }

  const group = new THREE.Group();
  variants.forEach((geometry, v) => {
    if (!placed[v].length) return;
    const mesh = new THREE.InstancedMesh(geometry, material, placed[v].length);
    placed[v].forEach((matrix, i) => { mesh.setMatrixAt(i, matrix); mesh.setColorAt(i, tints[v][i]); });
    geometry.setAttribute('brickUv', new THREE.InstancedBufferAttribute(new Float32Array(windows[v]), 4));
    group.add(mesh);
  });
  return group;
}
